import { useHead } from "../services/common";

import Footer from "../components/Footer";
import { Header, InputDateTime, Loading } from "../components";
import Content from "../components/Content";
import { useEffect, useRef, useState } from "react";
import { Education, Reservation } from "../../types";
import moment from "moment";
import { api, useApi } from "../services/api";
import { useUser } from "../User";
import { useSSRHook } from "ssr-hook";
import { Link } from "easy-page-router/react";
import Icon from "@mdi/react";
import { mdiImageArea, mdiMapMarker, mdiPencil, mdiPlus } from "@mdi/js";
import Modal from "react-responsive-modal";
import { resizeImage } from "easy-image-resizer";

export const emptyEducation: Education = {
    _id: "",
    title: "",
    contentId: "",
    active: true,
    picture: {
        type: "EASY_DB_FILE",
        url: "/logo-black.webp",
    },
    dateFrom: moment().add(1, "month").hours(17).minute(0).format("YYYY-MM-DDTHH:mm"),
    dateTo: moment().add(1, "month").hours(19).minute(0).format("YYYY-MM-DDTHH:mm"),
    place: ""
};

export default function EducationPage() {
    const user = useUser();

    const [educations, error, isLoading, reload] = useSSRHook<Education[]>(`/api/educations`);

    const title = "Vzdělávání";
    useHead(title, `${title} | Jilemnicko - společenství obcí`);

    const [edit, setEdit] = useState<null | Education>(null);

    return <>
        <Header title={title} background="vzdelavani-2" />
        <main>
            <section className="content-page">
                <h1 className="hidden">{title}</h1>

                <Content id="education" />

                {user.isAdmin && <>
                    <button className="center" onClick={() => setEdit(emptyEducation)}>
                        <Icon path={mdiPlus} size={1} /> Přidat kurz
                    </button>

                    <EditEducation
                        education={edit}
                        onChange={() => {
                            setEdit(null)
                            reload();
                        }}
                        onClose={() => setEdit(null)}
                    />
                </>}

                {isLoading && <Loading />}
                {error && <p className="alert">Seznam kurzů se bohužel nepodařili načíst 🙁</p>}
                {educations && educations.length === 0 && <p className="info">Zatím zde nejsou žádné kurzy.</p>}
                {educations && <div className="list-education">
                    {educations
                        .filter(e => e.active)
                        .sort((a, b) => a.dateFrom.localeCompare(b.dateFrom))
                        .map(e => <div key={e._id} className="item-education">
                            {user.isAdmin && <div className="admin small">
                                <button onClick={() => setEdit(e)}>
                                    <Icon path={mdiPencil} size={1} />
                                </button>
                            </div>}
                            <Link to={`/kurz/${e._id}`}>
                                <div className="top">
                                    <div className="date"><EducationDate {...e} /></div>
                                    <div className="picture" style={{ backgroundImage: `url(${e.picture.url})` }} />
                                </div>
                                <div className="text">
                                    {e.place && <div className="place"><Icon path={mdiMapMarker} size={0.7} color="#0f9759" /> {e.place}</div>}
                                    <h2>{e.title}</h2>
                                    <p>Více informací →</p>
                                </div>
                            </Link>
                        </div>)}
                </div>}

                {user.isAdmin && educations && educations.filter(e => !e.active).length > 0 && <>
                    <div style={{ height: "50px" }} />
                    <h2>Neaktivní kurzy</h2>
                    <p>Tuto nabídku vidí pouze administrátoři</p>

                    <div className="list-education">
                        {educations
                            .filter(e => !e.active)
                            .sort((a, b) => b.dateFrom.localeCompare(a.dateFrom))
                            .map(e => <div key={e._id} className="item-education">
                                {user.isAdmin && <div className="admin small">
                                    <button onClick={() => setEdit(e)}>
                                        <Icon path={mdiPencil} size={1} />
                                    </button>
                                </div>}
                                <Link to={`/kurz/${e._id}`}>
                                    <div className="top" style={{ backgroundImage: `url(${e.picture.url})` }}>
                                        <div className="date"><EducationDate {...e} /></div>
                                    </div>
                                    <div className="text">
                                        {e.place && <div className="place"><Icon path={mdiMapMarker} size={0.7} color="#0f9759" /> {e.place}</div>}
                                        <h2>{e.title}</h2>
                                        <p>Více informací →</p>
                                    </div>
                                </Link>
                            </div>)}
                    </div>
                </>}
            </section>
        </main>
        <Footer />
    </>;
}

export function EducationDate(education: Pick<Education, "dateFrom" | "dateTo">) {
    const dayFrom = moment(education.dateFrom).format("LL");
    const timeFrom = moment(education.dateFrom).format("HH:mm");
    const dayTo = moment(education.dateTo).format("LL");
    const timeTo = moment(education.dateTo).format("HH:mm");

    return `${dayFrom} ${timeFrom} - ${dayFrom === dayTo ? "" : `${dayTo} `}${timeTo}`;
}

export function EditEducation({ education, onChange, onClose }: {
    education: null | Education,
    onChange: (education: Education) => void,
    onClose: () => void
}) {

    const [edit, setEdit] = useState<Education>(education || emptyEducation);
    const [message, setMessage] = useState("");

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setEdit(education || emptyEducation);
        setMessage("");
    }, [education]);

    async function handleSave() {
        if (!edit) return;

        if (edit._id) {
            // update
            const [, error] = await api.updateEducation(edit);
            if (error) {
                setMessage("Kurz se nepodařilo upravit");
                return;
            }

            setMessage("Kurz byl úspěšně uložen.");
        } else {
            // new
            const [, error] = await api.addEducation(edit);
            if (error) {
                setMessage("Kurz se nepodařilo přidat");
                return;
            }

            setMessage("Kurz byl úspěšně přidán.");
        }


        onChange(edit);
    }

    return <Modal
        open={education !== null}
        onClose={onClose}
        styles={{ modal: { width: "550px" } }}
    >
        {edit && <>
            <h3>Kurz</h3>
            {message && <p className="alert">{message}</p>}

            <div style={{ backgroundImage: `url(${edit.picture.url})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", height: "300px", width: "370px", margin: "0px auto" }} />
            <button
                style={{ width: "100%" }}
                onClick={() => inputRef.current?.click()}
            >
                <Icon path={mdiImageArea} size={1} /> Změnit obrázek kurzu
            </button>
            <input
                className="hidden"
                type="file"
                ref={inputRef}
                accept="image/jpeg,image/png,image/webp"

                onChange={async (e) => {
                    const file = e.target.files?.[0] as File;
                    if (!file) return;

                    const url = await resizeImage(file, {
                        maxWidth: 1200,
                        maxHeight: 800,
                        type: "webp",
                    });

                    setEdit({ ...edit, picture: { type: "EASY_DB_FILE", url } });
                }}
            />
            <div style={{ height: "20px" }} />
            <input
                type="text"
                placeholder="Název kurzu"
                value={edit.title}
                onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            />
            <input
                type="text"
                placeholder="Místo konání"
                value={edit.place}
                onChange={(e) =>
                    setEdit({ ...edit, place: e.target.value })}
            />
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <InputDateTime value={edit.dateFrom} onChange={dateFrom => setEdit({ ...edit, dateFrom })} />
                <div style={{ marginBottom: "20px" }}> až </div>
                <InputDateTime value={edit.dateTo} onChange={dateTo => setEdit({ ...edit, dateTo })} />
            </div>
            <label>
                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
                Kurz je v aktuální nabídce
            </label>
            <div className="buttons">
                <button onClick={handleSave}>Uložit kurz</button>
            </div>
        </>}
    </Modal>
}